import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';

// Logo component
const Logo = ({ className = "h-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_smartgrid-hub-3/artifacts/huzue581_Nexintell%20logo.png"
    alt="Nexintell" 
    className={`${className} object-contain`}
  />
);

export default function PrivacyPolicy() {
  const lastUpdated = "January 26, 2025";

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="privacy-policy-page">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a href="/" className="flex items-center" data-testid="logo-link">
              <Logo className="h-8 md:h-10" />
            </a>
            <a 
              href="/" 
              className="flex items-center gap-2 text-[#a1a1a1] hover:text-white transition-colors"
              data-testid="back-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </a>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Shield className="h-8 w-8 text-[#ff4b2a]" />
              <h1 className="text-3xl md:text-4xl font-bold" data-testid="privacy-title">
                Privacy Policy
              </h1>
            </div>
            
            <p className="text-[#a1a1a1] mb-8">
              Last updated: {lastUpdated}
            </p>

            <div className="prose prose-invert max-w-none space-y-8">
              {/* Introduction */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">1. Introduction</h2>
                <p className="text-[#a1a1a1] leading-relaxed">
                  Nexintell Limited ("Nexintell", "we", "us", or "our") is committed to protecting your privacy and ensuring 
                  the security of your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                  your information when you visit our website or use our services, in compliance with the General Data Protection 
                  Regulation (GDPR) and the UK Data Protection Act 2018.
                </p>
                <p className="text-[#a1a1a1] leading-relaxed mt-4">
                  Nexintell Limited is a company registered in the United Kingdom and acts as the data controller for the 
                  personal data processed through our website and services.
                </p>
              </section>

              {/* Information We Collect */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">2. Information We Collect</h2>
                
                <h3 className="text-lg font-medium mb-3 text-[#ff4b2a]">2.1 Information You Provide</h3>
                <ul className="list-disc list-inside text-[#a1a1a1] space-y-2 mb-4">
                  <li><strong className="text-white">Demo Request Information:</strong> Name, email address, company name, job role, and any messages you provide when requesting a demo.</li>
                  <li><strong className="text-white">Contact Information:</strong> Information you provide when contacting us for partnerships or inquiries.</li>
                  <li><strong className="text-white">Account Information:</strong> Login credentials for admin users.</li>
                </ul>

                <h3 className="text-lg font-medium mb-3 text-[#ff4b2a]">2.2 Information Collected Automatically</h3>
                <ul className="list-disc list-inside text-[#a1a1a1] space-y-2">
                  <li><strong className="text-white">Usage Data:</strong> Information about how you interact with our website, including pages visited, time spent, and click patterns (CTA tracking).</li>
                  <li><strong className="text-white">Device Information:</strong> Browser type, operating system, device type, and user agent.</li>
                  <li><strong className="text-white">Log Data:</strong> IP address, access times, referring URLs, and pages viewed.</li>
                </ul>
              </section>

              {/* Legal Basis */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">3. Legal Basis for Processing (GDPR Article 6)</h2>
                <p className="text-[#a1a1a1] leading-relaxed mb-4">We process your personal data on the following legal bases:</p>
                <ul className="list-disc list-inside text-[#a1a1a1] space-y-2">
                  <li><strong className="text-white">Consent:</strong> When you submit a demo request or contact form, you consent to us processing your data.</li>
                  <li><strong className="text-white">Legitimate Interests:</strong> We may process data for our legitimate business interests, such as improving our services and website analytics.</li>
                  <li><strong className="text-white">Contract Performance:</strong> To provide services you have requested.</li>
                  <li><strong className="text-white">Legal Obligation:</strong> Where required by applicable laws.</li>
                </ul>
              </section>

              {/* How We Use Information */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">4. How We Use Your Information</h2>
                <ul className="list-disc list-inside text-[#a1a1a1] space-y-2">
                  <li>To respond to your demo requests and inquiries</li>
                  <li>To send you confirmation emails and follow-up communications</li>
                  <li>To analyze website usage and improve our services</li>
                  <li>To track CTA effectiveness and optimize user experience</li>
                  <li>To maintain security and prevent fraud</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              {/* Data Sharing */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">5. Data Sharing and Disclosure</h2>
                <p className="text-[#a1a1a1] leading-relaxed mb-4">
                  We do not sell your personal data. We may share your information with:
                </p>
                <ul className="list-disc list-inside text-[#a1a1a1] space-y-2">
                  <li><strong className="text-white">Service Providers:</strong> Third-party vendors who assist with email delivery (Resend), hosting, and analytics.</li>
                  <li><strong className="text-white">Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
                  <li><strong className="text-white">Business Transfers:</strong> In connection with any merger, acquisition, or sale of company assets.</li>
                </ul>
              </section>

              {/* Data Retention */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">6. Data Retention</h2>
                <p className="text-[#a1a1a1] leading-relaxed">
                  We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, 
                  typically for a period of 3 years from your last interaction with us, unless a longer retention period is required 
                  by law. CTA tracking data is retained for analytics purposes for up to 2 years.
                </p>
              </section>

              {/* Your Rights */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">7. Your Rights Under GDPR</h2>
                <p className="text-[#a1a1a1] leading-relaxed mb-4">
                  Under the GDPR, you have the following rights regarding your personal data:
                </p>
                <ul className="list-disc list-inside text-[#a1a1a1] space-y-2">
                  <li><strong className="text-white">Right of Access:</strong> Request a copy of your personal data we hold.</li>
                  <li><strong className="text-white">Right to Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
                  <li><strong className="text-white">Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten").</li>
                  <li><strong className="text-white">Right to Restriction:</strong> Request limitation of processing your data.</li>
                  <li><strong className="text-white">Right to Data Portability:</strong> Receive your data in a structured, machine-readable format.</li>
                  <li><strong className="text-white">Right to Object:</strong> Object to processing based on legitimate interests.</li>
                  <li><strong className="text-white">Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent.</li>
                </ul>
                <p className="text-[#a1a1a1] leading-relaxed mt-4">
                  To exercise any of these rights, please contact us at <a href="mailto:privacy@nexintell.co" className="text-[#ff4b2a] hover:underline">privacy@nexintell.co</a>.
                </p>
              </section>

              {/* Cookies */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">8. Cookies and Tracking Technologies</h2>
                <p className="text-[#a1a1a1] leading-relaxed mb-4">
                  We use essential cookies and tracking technologies to:
                </p>
                <ul className="list-disc list-inside text-[#a1a1a1] space-y-2 mb-4">
                  <li>Maintain user sessions and authentication</li>
                  <li>Track CTA interactions for analytics purposes</li>
                  <li>Remember your preferences</li>
                </ul>
                <p className="text-[#a1a1a1] leading-relaxed">
                  You can control cookies through your browser settings. Note that disabling certain cookies may affect website functionality.
                </p>
              </section>

              {/* International Transfers */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">9. International Data Transfers</h2>
                <p className="text-[#a1a1a1] leading-relaxed">
                  Your data may be transferred to and processed in countries outside the UK/EEA. When we transfer data internationally, 
                  we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the European Commission 
                  or UK International Data Transfer Agreements.
                </p>
              </section>

              {/* Security */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">10. Data Security</h2>
                <p className="text-[#a1a1a1] leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal data, including:
                </p>
                <ul className="list-disc list-inside text-[#a1a1a1] space-y-2 mt-4">
                  <li>Encryption of data in transit (HTTPS/TLS)</li>
                  <li>Secure password hashing for authentication</li>
                  <li>Access controls and authentication requirements</li>
                  <li>Regular security assessments and monitoring</li>
                </ul>
              </section>

              {/* Children */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">11. Children's Privacy</h2>
                <p className="text-[#a1a1a1] leading-relaxed">
                  Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal data 
                  from children. If you believe we have collected data from a child, please contact us immediately.
                </p>
              </section>

              {/* Changes */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">12. Changes to This Policy</h2>
                <p className="text-[#a1a1a1] leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting 
                  the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
                </p>
              </section>

              {/* Contact */}
              <section className="glass-panel rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-4 text-white">13. Contact Us</h2>
                <p className="text-[#a1a1a1] leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy or our data practices, or wish to exercise your rights, please contact us:
                </p>
                <div className="text-[#a1a1a1] space-y-2">
                  <p><strong className="text-white">Nexintell Limited</strong></p>
                  <p>Data Protection Officer</p>
                  <p>Email: <a href="mailto:privacy@nexintell.co" className="text-[#ff4b2a] hover:underline">privacy@nexintell.co</a></p>
                </div>
                <p className="text-[#a1a1a1] leading-relaxed mt-4">
                  You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe 
                  your data protection rights have been violated: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#ff4b2a] hover:underline">ico.org.uk</a>
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center text-sm text-[#a1a1a1]">
          © {new Date().getFullYear()} Nexintell Ltd. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
